import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthBiogems } from '@cores/security/hooks/useAuthBiogems.js';
import { transformReservations, getUniqueReservations } from './utilData.js';

/**
 * This hook manages all business logic and state for the reservations selection process.
 * It is responsible for:
 * - Fetching user/reservation data.
 * - Transforming data (grouping by cluster, finding unique reservations).
 * - Managing all selection state (selectedReservations, selectedClusters).
 * - Providing all event handlers for the panel (select, deselect, search, cluster, continue).
 */
export const useReservations = () => {
    const navigate = useNavigate();
    const { user, setCurrentReservations, currentReservations } = useAuthBiogems(); //console.log("currentReservations",currentReservations);
    /** currentReservations is an array of object, now we need ids, currentReservationIds */
    const currentReservationIds=currentReservations.map(rd=>rd.id); 
    // --- State Management ---
    const [selectedReservations, setSelectedReservations] = useState(currentReservationIds); //
    const [selectedClusters, setSelectedClusters] = useState([]); //
    const [error, setError] = useState(''); //

    // --- Data Transformation (Memoized) ---
    const reservationsObjByCluster = useMemo(() => {
        return transformReservations(user || {}); //
    }, [user?.reservations, user?.clusters]); //

    const allReservations = useMemo(() => {
        return getUniqueReservations(user || {}); //
    }, [user]); //

    const recentReservations = user?.recents; //

    // --- Core Business Logic & Handlers ---

    /**
     * Master handler for selecting/deselecting a single reservation.
     * This is the single source of truth for item selection.
     */
    const handleSelection = useCallback((resId) => {
        setSelectedReservations(prev => { //
            let newSelected;
            if (prev.includes(resId)) {
                newSelected = prev.filter(id => id !== resId); //
            } else {
                newSelected = [resId, ...prev.filter(id => id !== resId)]; //
            }
            
            // After updating selectedReservations, sync selectedClusters
            setSelectedClusters(() => { //
                const clustersWithSelected = Object.keys(reservationsObjByCluster).filter(clusterName => {
                    const clusterIds = reservationsObjByCluster[clusterName].map(r => r.id);
                    return clusterIds.some(id => newSelected.includes(id));
                });
                return clustersWithSelected;
            });
            return newSelected;
        });
    }, [reservationsObjByCluster]); //

    /**
     * Handler for selecting an item from the search dropdown.
     * Ensures the selected item is moved to the top of the list.
     */
    const handleSearchSelect = useCallback((reservation) => {
        if (!reservation) return;
        setSelectedReservations(prev => { //
            const filtered = prev.filter(id => id !== reservation.id);
            const newSelected = [reservation.id, ...filtered]; //
            
            // Sync cluster selection
            setSelectedClusters(() => { //
                const clustersWithSelected = Object.keys(reservationsObjByCluster).filter(clusterName => {
                    const clusterIds = reservationsObjByCluster[clusterName].map(r => r.id);
                    return clusterIds.some(id => newSelected.includes(id));
                });
                return clustersWithSelected;
            });
            return newSelected;
        });
    }, [reservationsObjByCluster]); //

    /**
     * Handler for selecting a cluster button.
     * Supports multi-select with Ctrl/Cmd key.
     */
    const handleClusterSelect = useCallback((event, cluster) => {
        if (event && (event.ctrlKey || event.metaKey)) { //
            // Multi-select with Ctrl/Cmd
            setSelectedClusters(prev => {
                if (prev.includes(cluster)) {
                    // Deselect
                    const newClusters = prev.filter(c => c !== cluster); //
                    // Remove cluster reservations from selectedReservations
                    if (reservationsObjByCluster[cluster]) {
                        const clusterIds = reservationsObjByCluster[cluster].map(r => r.id);
                        setSelectedReservations(prevSel => prevSel.filter(id => !clusterIds.includes(id))); //
                    }
                    return newClusters;
                } else {
                    // Add
                    const newClusters = [...prev, cluster]; //
                    if (reservationsObjByCluster[cluster]) {
                        const clusterIds = reservationsObjByCluster[cluster].map(r => r.id);
                        setSelectedReservations(prevSel => {
                            const filtered = prevSel.filter(id => !clusterIds.includes(id));
                            return [...clusterIds, ...filtered]; //
                        });
                    }
                    return newClusters;
                }
            });
        } else {
            // Single select (no Ctrl): select only this cluster
            setSelectedClusters([cluster]); //
            if (reservationsObjByCluster[cluster]) {
                const clusterIds = reservationsObjByCluster[cluster].map(r => r.id);
                setSelectedReservations(clusterIds); //
            } else {
                setSelectedReservations([]); //
            }
        }
    }, [reservationsObjByCluster]); //
    
    const handleSelectAll = useCallback(() => {
        setSelectedReservations(allReservations.map(r => r.id)); //
        setSelectedClusters(Object.keys(reservationsObjByCluster)); //
    }, [allReservations, reservationsObjByCluster]); //

    const handleDeselectAll = useCallback(() => {
        setSelectedReservations([]); //
        setSelectedClusters([]); //
    }, []); //

    /**
     * Handler for the "Continue" button.
     * Persists the selected reservations and navigates.
     */
    const handleContinue = useCallback(async (data) => {
        const idsToProcess = data?.ids || [];
        //console.log("handleContinue idsToProcess selectedReservations ",idsToProcess,selectedReservations);
       if (idsToProcess.length > 0) {
        try {
            setError(''); 
            
            // 1. Find the corresponding full reservation objects
            const reservationsToSave = allReservations.filter(res => 
                idsToProcess.includes(res.id)
            );
            // 2. Pass the list of full objects to the context provider
            // NOTE: setCurrentReservations must be updated in your provider to handle objects.
            const success = await setCurrentReservations(reservationsToSave); 
            
            // console.log("Reservations saved to context:", reservationsToSave);
               if (success) {
                   navigate('/app'); //
               }
            } catch (error) {
               console.error('Error opening reservations:', error);
               setError('Failed to open reservations'); //
            }
        }
    }, [navigate, setCurrentReservations, allReservations, setError]); //
    
    // --- Derived State (Memoized) ---

    /**
     * Creates the final list for the panel, with selected items first.
     */
    const reservationList = useMemo(() => {
        const selectedSet = new Set(selectedReservations);
        const selectedItems = selectedReservations
          .map(id => allReservations.find(res => res.id === id))
          .filter(Boolean); //
        const rest = allReservations.filter(res => !selectedSet.has(res.id)); //
        return [...selectedItems, ...rest]; //
    }, [selectedReservations, allReservations]); //

    // --- Return Prop Bag ---
    // This object contains everything the UI components need.
    return {
        // Data
        allReservations,
        reservationsObjByCluster,
        recentReservations,
        reservationList,

        // State
        selectedReservations,
        selectedClusters,
        error,
        
        // State Setters
        setError,
        
        // Handlers
        handlers: {
            handleSelection,
            handleSearchSelect,
            handleClusterSelect,
            handleSelectAll,
            handleDeselectAll,
            handleContinue,
        }
    };
};